/**
 * expectedRefusal.js
 *
 * THE ONE STATEMENT OF "THIS REFUSAL IS AN ANSWER, NOT A FAILURE".
 *
 * Some typed refusals are outcomes the client handles on its own: a read or a
 * press under a result the server no longer holds answers 422 `result_expired`,
 * the one handler re-runs the query (`filteredData.recoverExpiredResult`), and the
 * member is told to press again. Nothing failed. Logging it at error level puts
 * console errors on a handled path and pushes an `api_error` that evicts a real
 * failure from the 20-slot diagnostics ring (`services/clientDiagnostics`).
 *
 * Every seam that would log or record a failure asks here first: `apiClient`
 * (its "API request failed" line and its ring push), the unlock controls and the
 * page unlock store. A new expected kind is ONE entry in `EXPECTED_REFUSALS`, and
 * every seam is quiet for it; anything the list does not name still logs.
 *
 * `expectedStatuses` (an `apiClient` option) stays the per-CALL opt-out, for a
 * status one route answers by contract. This is the per-KIND rule, for a typed
 * code every route answers the same way.
 *
 * A LEAF MODULE: it imports the typed test and nothing else, so `apiClient`, a
 * store and a component can each load it without closing a cycle. It reads the
 * test from its pure leaf, never from the stateful per-result channel
 * (`utils/resultExpired`), because `apiClient` puts it in the public pages' boot
 * closure (th-jou3tw).
 */
import { isResultExpiredError } from '@/utils/resultExpiredError'

// kind → the test of an error as `apiClient.baseFetch` rejects it.
const EXPECTED_REFUSALS = Object.freeze({
  result_expired: isResultExpiredError,
})

// How far a wrapped error is looked for: an emitted payload wraps the error once
// (`{ error }`), and a host that re-emits it wraps that once more (`{ details }`).
const MAX_DEPTH = 3

/**
 * The expected kind a failure is, or null for a real one.
 *
 * Reads the error itself, a payload that names its kind (`{ refusal }`, as
 * `UnlockControl`'s `unlock-error` does, whose `error` is a message string), and
 * a payload that wraps either (`{ error }`, `{ details }`).
 *
 * @param {unknown} failure - an error, or an emitted payload that carries one
 * @returns {?string} a key of `EXPECTED_REFUSALS`
 */
export function expectedRefusalKind(failure, depth = 0) {
  if (!failure || typeof failure !== 'object' || depth > MAX_DEPTH) return null
  for (const [kind, test] of Object.entries(EXPECTED_REFUSALS)) {
    if (test(failure)) return kind
  }
  if (typeof failure.refusal === 'string' && Object.hasOwn(EXPECTED_REFUSALS, failure.refusal)) {
    return failure.refusal
  }
  return expectedRefusalKind(failure.error, depth + 1) ?? expectedRefusalKind(failure.details, depth + 1)
}

/**
 * @param {unknown} failure - as `expectedRefusalKind`
 * @returns {boolean} true when the failure is a handled answer
 */
export function isExpectedRefusal(failure) {
  return expectedRefusalKind(failure) !== null
}

/**
 * Log a failure at error level, unless it is an expected refusal.
 *
 * @param {string} label - the line's prefix; never a token or a context id
 * @param {unknown} failure - as `expectedRefusalKind`
 * @returns {boolean} true when it was logged (a real failure)
 */
export function logUnexpectedFailure(label, failure) {
  if (isExpectedRefusal(failure)) return false
  console.error(label, failure)
  return true
}
